import { useEffect, useMemo, useState } from "react";
import {
  getNatureR03RuntimeState,
  subscribeNatureR03RuntimeState,
} from "./runtime/NatureR03RuntimeRegistry";
import type {
  NatureR03Result,
  NatureR03RuntimeState,
} from "./runtime/NatureR03RuntimeTypes";

function isNatureR03Result(value: unknown): value is NatureR03Result {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<NatureR03Result>;
  return (
    typeof candidate.status === "string" &&
    Array.isArray(candidate.candidates) &&
    candidate.scoreType === "COSINE_SIMILARITY_NOT_PROBABILITY"
  );
}

function runtimeLabel(state: NatureR03RuntimeState) {
  if (!state.accessReady) return "Esperando cámara compartida";
  if (state.engine === "LOADING") return "Preparando reconocimiento offline";
  if (state.engine === "ERROR") return "Motor Nature no disponible";
  if (state.engine !== "READY") return "Preparando motor Nature";
  return "Analizando en vivo";
}

function resultTitle(result: NatureR03Result | null) {
  if (!result) return null;
  if (result.status === "POSIBLE_COINCIDENCIA") return "Posible coincidencia";
  if (result.status === "COINCIDENCIA_DEBIL_REVISAR") return "Coincidencia débil · revisar";
  if (result.status === "ESPECIE_NO_CUBIERTA") return "Especie no cubierta";
  return "Sin coincidencia suficiente";
}

export function NatureR03LivePanel() {
  const [runtime, setRuntime] = useState<NatureR03RuntimeState>(() => getNatureR03RuntimeState());
  const [raw, setRaw] = useState<NatureR03Result | null>(null);
  const [published, setPublished] = useState<NatureR03Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frameCount, setFrameCount] = useState(0);

  useEffect(() => {
    const unsubscribe = subscribeNatureR03RuntimeState(setRuntime);
    return () => { unsubscribe(); };
  }, []);

  useEffect(() => {
    const onRaw = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (!isNatureR03Result(detail)) return;
      setRaw(detail);
      setFrameCount((value) => value + 1);
      setError(null);
    };
    const onPublished = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (!isNatureR03Result(detail)) return;
      setPublished(detail);
      setError(null);
    };
    const onError = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      setError(String(detail ?? "Error de inferencia Nature"));
    };
    window.addEventListener("thera:nature-r03-raw-result", onRaw);
    window.addEventListener("thera:nature-r03-result", onPublished);
    window.addEventListener("thera:nature-r03-error", onError);
    return () => {
      window.removeEventListener("thera:nature-r03-raw-result", onRaw);
      window.removeEventListener("thera:nature-r03-result", onPublished);
      window.removeEventListener("thera:nature-r03-error", onError);
    };
  }, []);

  const stableResult = published?.status === "NO_CONCLUYENTE" ? null : published;
  const abstention = published?.status === "NO_CONCLUYENTE" ? published.abstentionMessage : null;
  const stabilizing = Boolean(
    raw &&
    raw.status !== "NO_CONCLUYENTE" &&
    (!published || published.timestamp !== raw.timestamp),
  );
  const title = resultTitle(published);
  const candidates = useMemo(() => stableResult?.candidates.slice(0, 3) ?? [], [stableResult]);

  return (
    <section
      className="thera-nature-r03-live"
      aria-live="polite"
      data-engine={runtime.engine.toLowerCase()}
      data-access-ready={runtime.accessReady ? "true" : "false"}
    >
      <header className="thera-nature-r03-live-head">
        <div>
          <strong>Identificación en vivo · R0.3</strong>
          <small>{runtimeLabel(runtime)}</small>
        </div>
        <span className={`thera-nature-r03-dot ${runtime.engine === "READY" && runtime.accessReady ? "ready" : ""}`} aria-hidden="true" />
      </header>

      <p className="thera-nature-r03-live-help">
        Apunta la cámara a una planta. Nature analiza fotogramas de la cámara compartida sin detener el video.
      </p>

      {error ? (
        <div className="thera-nature-r03-live-card error" role="status">
          <strong>No se pudo analizar este fotograma</strong>
          <span>{error}</span>
        </div>
      ) : null}

      {!error && runtime.accessReady && runtime.engine === "READY" && !published ? (
        <div className="thera-nature-r03-live-card scanning">
          <strong>Analizando imagen en vivo…</strong>
          <span>Mantén la planta dentro de la cámara unos segundos.</span>
        </div>
      ) : null}

      {!error && stabilizing ? (
        <div className="thera-nature-r03-live-card scanning">
          <strong>Estabilizando coincidencia…</strong>
          <span>Nature espera consistencia entre varios fotogramas antes de mostrar una especie.</span>
        </div>
      ) : null}

      {!error && abstention ? (
        <div className="thera-nature-r03-live-card neutral">
          <strong>{title}</strong>
          <span>{abstention}</span>
        </div>
      ) : null}

      {!error && stableResult && candidates.length ? (
        <div className="thera-nature-r03-live-card result">
          <span className="thera-nature-r03-kicker">{title}</span>
          <strong className="thera-nature-r03-species">{candidates[0].scientificName}</strong>
          <span>Similitud coseno {candidates[0].similarity.toFixed(4)}</span>
          <ol>
            {candidates.map((candidate) => (
              <li key={`${candidate.rank}-${candidate.scientificName}`}>
                <span>{candidate.rank}. {candidate.scientificName}</span>
                <small>{candidate.similarity.toFixed(4)}</small>
              </li>
            ))}
          </ol>
          <small className="thera-nature-r03-disclaimer">
            Resultado experimental. La similitud no es una probabilidad y requiere verificación de campo.
          </small>
        </div>
      ) : null}

      <footer className="thera-nature-r03-live-meta">
        <span>R0.3 · {runtime.referenceSpecies} especies</span>
        <span>{frameCount} análisis</span>
      </footer>
    </section>
  );
}
